import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Card, Tabs, Spin, Empty, Tag, Typography, Space, Button, Table, List, Alert,
    Row, Col, Upload, message, Modal, Collapse, Statistic,
} from "antd";
import { ArrowLeftOutlined, DownloadOutlined, UploadOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { salesRevenueApi } from "../../../api/salesRevenueApi";
import { useAuth } from "../../../hooks/useAuth";
import CargoReportDetail from "../../../components/cargo/CargoReportDetail";
import "./CargoReportArchivePage.css";

const { Text, Title } = Typography;

// 二期改名：货运文件权限（下载原件/打包/报文 TXT + 人工补传上传）
const FILE_PERMISSION = "sales:cargo:file";
const MAX_PACKAGE_FILES = 100;
const MAX_PACKAGE_MB = 200;

// 人工补传上限（与后端一致）
const MAX_UPLOAD_FILES = 10;
const MAX_UPLOAD_FILE_MB = 20;
const MAX_UPLOAD_TOTAL_MB = 50;
const CGO_FILENAME_RE = /(舱单|装载舱单|装载重量|cargo\s*manifest|load\s*statement)\.(docx?|xlsx?)$/i;

const SOURCE_KIND_TEXT = {
    MAIL_ATTACHMENT: "邮件附件",
    MANUAL_UPLOAD: "人工上传",
    MESSAGE_BODY: "标准报文",
    HISTORICAL_RECORD: "历史不可追溯",
};

const DOCUMENT_TYPE_TEXT = {
    HKG_CARGO_ATTACHMENT_MANIFEST: "香港货运附件舱单",
    HKG_CARGO_ATTACHMENT_AWB: "香港货运附件运单",
    // 联调库偶发旧值：临时识别，正式口径只认新格式名
    POST_FLIGHT_DOC_MANIFEST: "香港货运附件舱单",
    POST_FLIGHT_DOC_AWB: "香港货运附件运单",
    ULD_LOAD_STATEMENT: "ULD 装载重量表",
    CARGO_MANIFEST_NO_ULD: "舱单（无 ULD）",
    CARGO_MANIFEST_ULD: "舱单（含 ULD）",
    CPM_MESSAGE: "CPM 报文",
    FFM_MESSAGE: "FFM 报文",
    FWB_MESSAGE: "FWB 报文",
    OTHER_CARGO_FILE: "其他货运文件",
};

const FACT_STATUS_TEXT = {
    COMPLETE: { color: "green", text: "完整" },
    PARTIAL: { color: "orange", text: "部分完整" },
    INCOMPLETE: { color: "orange", text: "不完整" },
    MISSING: { color: "default", text: "缺失" },
    CONFLICT: { color: "red", text: "同口径冲突" },
    NO_DATA: { color: "default", text: "暂无货运资料" },
};

const PARSE_STATUS_TEXT = {
    PARSED: { color: "green", text: "已解析" },
    PARTIAL: { color: "orange", text: "部分解析" },
    PENDING: { color: "processing", text: "解析中" },
    FAILED: { color: "red", text: "解析失败" },
    SKIPPED_DUPLICATE: { color: "default", text: "内容相同未计入" },
    REJECTED: { color: "red", text: "已拒绝" },
    NOT_APPLICABLE: { color: "default", text: "不适用" },
};

function statusTag(status) {
    const cfg = FACT_STATUS_TEXT[status] || { color: "default", text: status || "—" };
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

function parseStatusTag(status) {
    if (!status) return <Tag>—</Tag>;
    const cfg = PARSE_STATUS_TEXT[status] || { color: "default", text: status };
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

function fmtNum(v, digits = 0) {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toLocaleString("zh-CN", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

function saveBlob(blob, filename) {
    if (!(blob instanceof Blob)) {
        throw new Error("下载失败：服务器未返回文件数据");
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

function resolveArchiveStatus(detail) {
    if (detail?.cargo_status) return detail.cargo_status;
    const fact = detail?.fact_summary;
    if (!fact) return "NO_DATA";
    const m = fact.manifest?.status;
    const l = fact.load?.status;
    if ((!m || m === "MISSING") && (!l || l === "MISSING") && !(detail.file_summary?.total_count > 0)) {
        return "NO_DATA";
    }
    if (m === "CONFLICT" || l === "CONFLICT" || l === "INCOMPLETE" || m === "PARTIAL") return "PARTIAL";
    if (m === "COMPLETE" && l === "COMPLETE") return "COMPLETE";
    return "PARTIAL";
}

async function readBlobErrorMessage(err) {
    const data = err?.response?.data;
    if (!data) return err?.message || "请求失败";
    if (typeof data === "string") return data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            const detail = parsed?.detail;
            if (typeof detail === "string") return detail;
            return detail?.message || parsed?.message || text;
        } catch {
            return "请求失败";
        }
    }
    const detail = data?.detail;
    if (typeof detail === "string") return detail;
    return detail?.message || data?.message || "请求失败";
}

function openSection(keys, key) {
    const set = new Set(keys || []);
    set.add(key);
    return Array.from(set);
}

function sectionElementId(key) {
    return `cargo-archive-section-${key}`;
}

const SECTION_ORDER = ["load", "files", "messages", "sources"];

function findSectionNode(key) {
    const byId = document.getElementById(sectionElementId(key));
    if (byId) return byId;
    const index = SECTION_ORDER.indexOf(key);
    if (index < 0) return null;
    return document.querySelectorAll(".cargo-archive-collapse > .ant-collapse-item")[index] || null;
}

const MISSING_FIELD_LABELS = {
    position_code: "舱位（货装在飞机哪个位置）",
    destination: "卸机站",
    weight_kg: "重量",
    net_weight_kg: "净重",
    contour: "板型",
    height: "高度",
    shc: "特货代码",
};

function loadMissingLabels(load) {
    if (Array.isArray(load?.missing_field_labels) && load.missing_field_labels.length > 0) {
        return load.missing_field_labels;
    }
    return (load?.missing_fields || []).map((field) => MISSING_FIELD_LABELS[field] || field);
}

export default function CargoReportArchivePage() {
    const { flightOperationId } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canDownload = hasPermission(FILE_PERMISSION);
    const canUpload = canDownload;

    const [detail, setDetail] = useState(null);
    const [messages, setMessages] = useState(null);
    const [files, setFiles] = useState(null);
    const [sourceVersions, setSourceVersions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [openKeys, setOpenKeys] = useState(["load"]);
    const [highlightFileId, setHighlightFileId] = useState(null);
    const [selectedFileIds, setSelectedFileIds] = useState([]);

    const revealSection = useCallback((key) => {
        setOpenKeys((prev) => openSection(prev, key));
        window.setTimeout(() => {
            const node = findSectionNode(key);
            if (!node) return;
            node.scrollIntoView({ behavior: "smooth", block: "start" });
            node.classList.add("cargo-archive-section--flash");
            window.setTimeout(() => {
                node.classList.remove("cargo-archive-section--flash");
            }, 1600);
        }, 80);
    }, []);

    const loadAll = useCallback((withSpin = true) => {
        if (!flightOperationId) return;
        if (withSpin) setLoading(true);
        Promise.all([
            salesRevenueApi.getCargoReportDetail(flightOperationId).catch(() => null),
            salesRevenueApi.getCargoReportMessages(flightOperationId).catch(() => null),
            salesRevenueApi.getCargoReportFiles(flightOperationId).catch(() => null),
            salesRevenueApi.getCargoReportSourceVersions(flightOperationId).catch(() => null),
        ])
            .then(([d, m, f, sv]) => {
                setDetail(d?.data ?? null);
                setMessages(m?.data ?? null);
                setFiles(f?.data ?? null);
                setSourceVersions(sv?.data ?? null);
                setSelectedFileIds([]);
            })
            .finally(() => {
                if (withSpin) setLoading(false);
            });
    }, [flightOperationId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const fileItems = files?.items || [];
    const downloadableItems = useMemo(
        () => fileItems.filter((f) => f.can_download),
        [fileItems],
    );

    const handleDownloadFile = useCallback(async (file) => {
        if (!canDownload || !file?.can_download) return;
        setDownloading(`file:${file.file_id}`);
        try {
            const res = await salesRevenueApi.downloadCargoReportFile(flightOperationId, file.file_id);
            saveBlob(res.data, file.filename || file.file_id);
        } catch (err) {
            message.error(await readBlobErrorMessage(err));
        } finally {
            setDownloading(null);
        }
    }, [canDownload, flightOperationId]);

    const handleDownloadTxt = useCallback(async (msg, messageType) => {
        if (!canDownload) return;
        setDownloading(`txt:${messageType}:${msg.id}`);
        try {
            const res = await salesRevenueApi.downloadCargoReportMessageTxt(
                flightOperationId,
                msg.id,
                messageType || msg.message_type || msg.load_type,
            );
            saveBlob(res.data, `${messageType || msg.load_type || "MSG"}_${msg.id}.txt`);
        } catch (err) {
            message.error(await readBlobErrorMessage(err));
        } finally {
            setDownloading(null);
        }
    }, [canDownload, flightOperationId]);

    const handlePackageDownload = useCallback(async (ids) => {
        if (!canDownload) return;
        const targetIds = ids?.length ? ids : selectedFileIds;
        if (!targetIds.length) {
            message.warning("请先选择要打包的文件");
            return;
        }
        if (targetIds.length > MAX_PACKAGE_FILES) {
            message.warning(`单次最多打包 ${MAX_PACKAGE_FILES} 个文件，请缩小范围`);
            return;
        }
        const selectedMeta = fileItems.filter((f) => targetIds.includes(f.file_id));
        const totalBytes = selectedMeta.reduce((sum, f) => sum + (Number(f.size_bytes) || 0), 0);
        if (totalBytes > MAX_PACKAGE_MB * 1024 * 1024) {
            message.warning(`所选文件合计超过 ${MAX_PACKAGE_MB} MB，请缩小范围`);
            return;
        }
        setDownloading("package");
        try {
            const res = await salesRevenueApi.downloadCargoReportPackage(flightOperationId, targetIds);
            const filename = `flight_${flightOperationId}_cargo_files.zip`;
            saveBlob(res.data, filename);
            const included = res.headers?.["x-package-included"];
            const excluded = res.headers?.["x-package-excluded"];
            const failed = res.headers?.["x-package-failed"];
            if (excluded > 0 || failed > 0) {
                message.info(`打包完成：成功 ${included || "?"}，排除 ${excluded || 0}，失败 ${failed || 0}（详见 ZIP 内 MANIFEST.json）`);
            } else {
                message.success("打包下载已开始");
            }
        } catch (err) {
            message.error(await readBlobErrorMessage(err));
        } finally {
            setDownloading(null);
        }
    }, [canDownload, flightOperationId, selectedFileIds, fileItems]);

    const handleManualUpload = useCallback(async (fileList) => {
        if (!canUpload || uploading) return;
        const picked = Array.from(fileList || []);
        if (!picked.length) return;
        if (picked.length > MAX_UPLOAD_FILES) {
            message.error(`单次最多上传 ${MAX_UPLOAD_FILES} 个文件，请缩小范围`);
            return;
        }
        for (const f of picked) {
            if (!CGO_FILENAME_RE.test(f.name || "")) {
                message.error(`「${f.name}」不符合 CGO 命名约定（需以「舱单 / 装载舱单 / 装载重量」等结尾的 Word/Excel）`);
                return;
            }
            if (f.size > MAX_UPLOAD_FILE_MB * 1024 * 1024) {
                message.error(`「${f.name}」超过单文件 ${MAX_UPLOAD_FILE_MB} MB 上限`);
                return;
            }
        }
        const totalBytes = picked.reduce((sum, f) => sum + (f.size || 0), 0);
        if (totalBytes > MAX_UPLOAD_TOTAL_MB * 1024 * 1024) {
            message.error(`所选文件合计超过 ${MAX_UPLOAD_TOTAL_MB} MB 上限，请缩小范围`);
            return;
        }
        setUploading(true);
        try {
            const res = await salesRevenueApi.uploadCargoReportFiles(flightOperationId, picked);
            const items = res?.data?.items || [];
            items.forEach((it) => {
                const name = it.original_filename || "文件";
                if (it.parse_status === "REJECTED") {
                    message.error(`「${name}」被拒收：${it.message || "不符合约定"}`);
                } else if (it.deduplicated) {
                    message.info(`「${name}」内容与已有文件相同，未重复计入货量`);
                } else if (it.parse_status === "FAILED") {
                    message.warning(`「${name}」${it.message || "解析失败；原件已保留，未覆盖已有成功事实"}`);
                } else if (it.flight_mismatch_warning) {
                    message.warning(`「${name}」${it.message || "内容指向的航班与当前档案不一致，已挂在本航班，请核对文件是否传错"}`);
                } else {
                    message.success(`「${name}」上传并解析成功，货量已更新`);
                }
            });
            revealSection("files");
            loadAll(false);
        } catch (err) {
            message.error(await readBlobErrorMessage(err));
        } finally {
            setUploading(false);
        }
    }, [canUpload, uploading, flightOperationId, loadAll, revealSection]);

    const handleReparseFile = useCallback(async (row) => {
        if (!canUpload || !row?.file_id) return;
        setDownloading(`reparse:${row.file_id}`);
        try {
            const res = await salesRevenueApi.reparseCargoReportFile(flightOperationId, row.file_id);
            const it = res?.data || {};
            const name = it.original_filename || row.filename || "文件";
            if (it.deduplicated) {
                message.info(`「${name}」内容与已有文件相同，未重复计入货量`);
            } else if (it.parse_status === "FAILED") {
                message.warning(`「${name}」${it.message || "重新解析失败"}`);
            } else if (it.flight_mismatch_warning) {
                message.warning(`「${name}」${it.message || "内容指向的航班与当前档案不一致，已挂在本航班"}`);
            } else {
                message.success(`「${name}」${it.message || "重新解析成功，货量已更新"}`);
            }
            loadAll(false);
        } catch (err) {
            message.error(await readBlobErrorMessage(err));
        } finally {
            setDownloading(null);
        }
    }, [canUpload, flightOperationId, loadAll]);

    const handleDeleteFile = useCallback((row) => {
        if (!canUpload || !row?.file_id) return;
        Modal.confirm({
            title: "删除补传文件？",
            content: `将删除「${row.filename || row.file_name || "该文件"}」的原件、相关货量事实与存储对象。邮件附件不可删；此操作不可恢复。`,
            okText: "删除",
            okType: "danger",
            cancelText: "取消",
            onOk: async () => {
                setDownloading(`delete:${row.file_id}`);
                try {
                    await salesRevenueApi.deleteCargoReportFile(flightOperationId, row.file_id);
                    message.success("已删除补传文件");
                    setSelectedFileIds((prev) => prev.filter((id) => id !== row.file_id));
                    loadAll(false);
                } catch (err) {
                    message.error(await readBlobErrorMessage(err));
                } finally {
                    setDownloading(null);
                }
            },
        });
    }, [canUpload, flightOperationId, loadAll]);

    const jumpToEvidence = useCallback((ev) => {
        if (ev?.source_attachment_id || ev?.source_upload_file_id || ev?.file_id) {
            setHighlightFileId(String(ev.source_attachment_id || ev.source_upload_file_id || ev.file_id));
            revealSection("files");
            return;
        }
        if (ev?.source_kind === "MESSAGE_BODY") {
            revealSection("messages");
            return;
        }
        revealSection("sources");
    }, [revealSection]);

    const uploadControl = canUpload ? (
        <Upload
            multiple
            accept=".doc,.docx,.xls,.xlsx"
            showUploadList={false}
            disabled={uploading}
            beforeUpload={(file, fileList) => {
                if (file === fileList[fileList.length - 1]) {
                    handleManualUpload(fileList);
                }
                return Upload.LIST_IGNORE;
            }}
        >
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
                补传约定文件
            </Button>
        </Upload>
    ) : null;

    if (loading) return <Spin style={{ display: "block", textAlign: "center", padding: 60 }} />;
    if (!detail) return <Empty description="该航班暂无货运档案" />;

    const identity = detail.identity || {};
    const fact = detail.fact_summary || {};
    const missingLabels = loadMissingLabels(fact.load);
    const issues = detail.issues || [];
    const evidence = detail.evidence || [];
    const fileSummary = detail.file_summary || { total_count: 0, downloadable_count: 0, parsed_count: 0 };
    const archiveStatus = resolveArchiveStatus(detail);
    const routeText = identity.route_display || "—";
    const fileHint = fileSummary.total_count > 0
        ? `文件 ${fileSummary.parsed_count ?? 0}/${fileSummary.total_count} 已解析`
        : "暂无文件";

    const loadPanel = (
        <div>
            <CargoReportDetail
                flightId={Number(flightOperationId)}
                reportData={detail}
                showSummary={false}
                canDownload={canDownload}
            />
            <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="装载与舱单分口径展示；装载毛重含板，不与运单重量对差。缺舱位时位置利用率保持未知。"
            />
        </div>
    );

    const messagePanel = (
        <div>
            {messages?.has_standard_original ? (
                <Tabs
                    items={["CPM", "FFM", "FWB"].map((type) => ({
                        key: type,
                        label: `${type}（${(messages[type.toLowerCase()] || []).length}）`,
                        children: (
                            <Table
                                size="small"
                                rowKey="id"
                                dataSource={messages[type.toLowerCase()] || []}
                                pagination={false}
                                columns={[
                                    { title: "版本", dataIndex: "version_number", width: 70, render: (v, r) => <Space>v{v}{r.is_current && <Tag color="green">当前</Tag>}</Space> },
                                    { title: "来源", dataIndex: "source_type", width: 100, render: (v) => v || "—" },
                                    { title: "解析状态", dataIndex: "parse_status", width: 100, render: (v) => parseStatusTag(v) },
                                    { title: "原文长度", dataIndex: "raw_text_length", width: 100, render: (v) => (v ? `${v} 字符` : "—") },
                                    { title: "更新时间", dataIndex: "updated_at", render: (v) => v || "—" },
                                    {
                                        title: "操作", width: 90, render: (_, r) => (
                                            canDownload && r.has_text ? (
                                                <Button size="small" type="link" icon={<DownloadOutlined />}
                                                    loading={downloading === `txt:${type}:${r.id}`}
                                                    onClick={() => handleDownloadTxt(r, type)}>下载 TXT</Button>
                                            ) : <Text type="secondary">{canDownload ? "无原文" : "需下载权限"}</Text>
                                        ),
                                    },
                                ]}
                            />
                        ),
                    }))}
                />
            ) : (
                <Empty description={messages?.attachment_only ? "未收到标准报文原文；当前货量事实由邮件附件或人工补传文件支撑（不在此冒充原文）" : "暂无报文原文"} />
            )}
        </div>
    );

    const filePanel = (
        <div>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`补传仅支持 CGO 约定 Word/Excel（单次 ${MAX_UPLOAD_FILES} 个、单文件 ${MAX_UPLOAD_FILE_MB} MB、合计 ${MAX_UPLOAD_TOTAL_MB} MB）。解析失败可「重新解析」；补传文件可「删除」。邮件附件不可删。打包上限：最多 ${MAX_PACKAGE_FILES} 个或合计 ${MAX_PACKAGE_MB} MB。`}
            />
            {canDownload && (
                <Space style={{ marginBottom: 12 }} wrap>
                    {uploadControl}
                    <Button
                        size="small"
                        disabled={!selectedFileIds.length}
                        loading={downloading === "package"}
                        icon={<DownloadOutlined />}
                        onClick={() => handlePackageDownload(selectedFileIds)}
                    >
                        下载已选（{selectedFileIds.length}）
                    </Button>
                    <Button
                        size="small"
                        disabled={!downloadableItems.length}
                        loading={downloading === "package"}
                        onClick={() => handlePackageDownload(downloadableItems.map((f) => f.file_id))}
                    >
                        整航班可下打包
                    </Button>
                </Space>
            )}
            <Table
                size="small"
                rowKey="file_id"
                dataSource={fileItems}
                pagination={false}
                rowClassName={(r) => (highlightFileId && r.file_id === highlightFileId ? "ant-table-row-selected" : "")}
                rowSelection={canDownload ? {
                    selectedRowKeys: selectedFileIds,
                    onChange: (keys) => setSelectedFileIds(keys),
                    getCheckboxProps: (r) => ({ disabled: !r.can_download }),
                } : undefined}
                columns={[
                    { title: "文件名", dataIndex: "filename", ellipsis: true, render: (v, r) => v || r.file_name || "—" },
                    {
                        title: "来源", dataIndex: "source_kind", width: 110,
                        render: (v, r) => {
                            if (v === "MANUAL_UPLOAD") {
                                return (
                                    <Space direction="vertical" size={0}>
                                        <Tag color="purple">人工上传</Tag>
                                        {r.uploaded_by_name && <Text type="secondary" style={{ fontSize: 12 }}>{r.uploaded_by_name}</Text>}
                                    </Space>
                                );
                            }
                            return SOURCE_KIND_TEXT[v] || "邮件附件";
                        },
                    },
                    {
                        title: "类型", dataIndex: "document_type", width: 150,
                        render: (v, r) => DOCUMENT_TYPE_TEXT[v] || DOCUMENT_TYPE_TEXT[r.source_format] || v || r.source_format || r.content_type || "—",
                    },
                    {
                        title: "收到时间", dataIndex: "received_at", width: 160,
                        render: (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—"),
                    },
                    { title: "来源主题", dataIndex: "source_subject", ellipsis: true, render: (v, r) => v || (r.source_kind === "MANUAL_UPLOAD" ? "（人工补传，无来源邮件）" : "—") },
                    { title: "大小", dataIndex: "size_bytes", width: 90, render: (v) => (v != null ? `${(v / 1024).toFixed(1)} KB` : "—") },
                    {
                        title: "解析状态", dataIndex: "parse_status", width: 120,
                        render: (v, r) => (
                            <Space direction="vertical" size={0}>
                                {parseStatusTag(v)}
                                {r.flight_mismatch && <Tag color="orange">航班不一致</Tag>}
                            </Space>
                        ),
                    },
                    {
                        title: "事实用途", dataIndex: "fact_usage", width: 120,
                        render: (v, r) => (v || r.data_roles || []).map((p) => <Tag key={p}>{p}</Tag>),
                    },
                    {
                        title: "操作", width: 220, render: (_, r) => {
                            const dl = r.can_download || r.is_downloadable;
                            const showReparse = canUpload && r.source_kind === "MANUAL_UPLOAD" && (r.can_reparse || r.parse_status === "FAILED");
                            const showDelete = canUpload && r.source_kind === "MANUAL_UPLOAD" && r.can_delete !== false;
                            return (
                                <Space size={0} wrap>
                                    {canDownload && dl ? (
                                        <Button size="small" type="link" icon={<DownloadOutlined />}
                                            loading={downloading === `file:${r.file_id}`}
                                            onClick={() => handleDownloadFile(r)}>下载</Button>
                                    ) : null}
                                    {showReparse ? (
                                        <Button size="small" type="link" icon={<ReloadOutlined />}
                                            loading={downloading === `reparse:${r.file_id}`}
                                            onClick={() => handleReparseFile(r)}>重新解析</Button>
                                    ) : null}
                                    {showDelete ? (
                                        <Button size="small" type="link" danger icon={<DeleteOutlined />}
                                            loading={downloading === `delete:${r.file_id}`}
                                            onClick={() => handleDeleteFile(r)}>删除</Button>
                                    ) : null}
                                    {!dl && canDownload && !showReparse && !showDelete ? (
                                        <Text type="secondary">{r.download_unavailable_reason || "不可下载"}</Text>
                                    ) : null}
                                    {!canDownload && !showReparse && !showDelete ? (
                                        <Text type="secondary">需货运文件权限</Text>
                                    ) : null}
                                </Space>
                            );
                        },
                    },
                ]}
            />
        </div>
    );

    const sourcesPanel = (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card size="small" title="来源证据" type="inner">
                {evidence.length === 0 ? (
                    <Empty description="暂无来源证据；历史记录可能不可追溯" />
                ) : (
                    <Table
                        size="small"
                        rowKey={(r) => `${r.cargo_message_id}-${r.source_attachment_id || r.source_upload_file_id || "body"}`}
                        dataSource={evidence}
                        pagination={false}
                        columns={[
                            {
                                title: "来源类型", width: 120,
                                render: (_, r) => {
                                    if (r.source_kind === "MAIL_ATTACHMENT") return "邮件附件";
                                    if (r.source_kind === "MANUAL_UPLOAD") {
                                        return (
                                            <Space direction="vertical" size={0}>
                                                <Tag color="purple">人工上传</Tag>
                                                {r.uploaded_by_name && (
                                                    <Text type="secondary" style={{ fontSize: 12 }}>{r.uploaded_by_name}</Text>
                                                )}
                                            </Space>
                                        );
                                    }
                                    if (r.source_kind === "HISTORICAL_RECORD") return "历史不可追溯";
                                    return "标准报文";
                                },
                            },
                            { title: "格式", dataIndex: "source_format", width: 160, render: (v) => v || "—" },
                            { title: "解析状态", dataIndex: "parse_status", width: 100, render: (v) => parseStatusTag(v) },
                            { title: "版本", dataIndex: "version_number", width: 70, render: (v, r) => <Space>v{v ?? "—"}{r.is_current && <Tag color="green">当前</Tag>}</Space> },
                            { title: "提供事实", dataIndex: "provided_fact_paths", render: (v) => (v || []).map((p) => <Tag key={p}>{p}</Tag>) },
                            { title: "更新时间", dataIndex: "updated_at", width: 170, render: (v) => v || "—" },
                            {
                                title: "操作", width: 100,
                                render: (_, r) => {
                                    if (r.source_kind === "HISTORICAL_RECORD"
                                        || (!r.source_attachment_id && !r.source_upload_file_id && !r.source_receipt_id)) {
                                        return <Text type="secondary">不可追溯</Text>;
                                    }
                                    return (
                                        <Button size="small" type="link" onClick={() => jumpToEvidence(r)}>
                                            {(r.source_attachment_id || r.source_upload_file_id) ? "查看文件" : "查看报文"}
                                        </Button>
                                    );
                                },
                            },
                        ]}
                    />
                )}
            </Card>

            <Card size="small" title="同口径冲突" type="inner">
                {(sourceVersions?.conflicts || []).length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无同口径冲突" />
                ) : (
                    (sourceVersions.conflicts || []).map((c, idx) => (
                        <Card key={idx} type="inner" size="small" title={c.message} style={{ marginBottom: 8 }}>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="cargo_message_id"
                                dataSource={c.sources || []}
                                columns={[
                                    { title: "格式", dataIndex: "source_format" },
                                    { title: "件数", dataIndex: "total_pieces", width: 80, render: (v) => v ?? "—" },
                                    { title: "重量 kg", dataIndex: "total_weight_kg", width: 100, render: (v) => v ?? "—" },
                                    { title: "净重 kg", dataIndex: "total_net_weight_kg", width: 100, render: (v) => v ?? "—" },
                                    { title: "时间", dataIndex: "updated_at", render: (v) => v || "—" },
                                    {
                                        title: "操作", width: 90,
                                        render: (_, r) => (r.source_attachment_id || r.source_upload_file_id) ? (
                                            <Button size="small" type="link" onClick={() => jumpToEvidence(r)}>查看文件</Button>
                                        ) : "—",
                                    },
                                ]}
                            />
                        </Card>
                    ))
                )}
            </Card>

            <Card size="small" title="同名不同内容（分版本）" type="inner">
                {(sourceVersions?.attachment_versions || []).length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无同名不同内容附件" />
                ) : (
                    (sourceVersions.attachment_versions || []).map((g) => (
                        <Card key={g.filename} type="inner" size="small" title={g.filename} style={{ marginBottom: 8 }}>
                            <Text type="secondary">{g.message}</Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(r) => `${r.file_id}-${r.cargo_message_id}`}
                                dataSource={g.versions || []}
                                columns={[
                                    { title: "版本", dataIndex: "version_number", width: 70 },
                                    { title: "解析状态", dataIndex: "parse_status", width: 100, render: (v) => parseStatusTag(v) },
                                    { title: "时间", dataIndex: "updated_at" },
                                    {
                                        title: "操作", width: 90,
                                        render: (_, r) => (
                                            <Button size="small" type="link" onClick={() => jumpToEvidence(r)}>查看文件</Button>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    ))
                )}
            </Card>

            <Card size="small" title="相同内容（重复到达）" type="inner">
                {(sourceVersions?.duplicates || []).length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无重复到达记录" />
                ) : (
                    (sourceVersions.duplicates || []).map((d) => (
                        <Card
                            key={d.content_hash}
                            type="inner"
                            size="small"
                            title="相同内容（重复到达）"
                            style={{ marginBottom: 8 }}
                        >
                            <Text type="secondary">{d.message}</Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(r) => `${r.file_id}-${r.cargo_message_id}`}
                                dataSource={d.arrivals || []}
                                columns={[
                                    { title: "文件名", dataIndex: "filename", ellipsis: true },
                                    { title: "时间", dataIndex: "updated_at" },
                                    { title: "当前", dataIndex: "is_current", width: 70, render: (v) => (v ? <Tag color="green">是</Tag> : "否") },
                                ]}
                            />
                        </Card>
                    ))
                )}
            </Card>

            <Card size="small" title="失败不覆盖成功" type="inner">
                {(sourceVersions?.failed_retained || []).length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无解析失败记录" />
                ) : (
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="cargo_message_id"
                        dataSource={sourceVersions.failed_retained}
                        columns={[
                            { title: "类型", dataIndex: "load_type", width: 70 },
                            { title: "格式", dataIndex: "source_format", width: 160 },
                            { title: "说明", dataIndex: "message" },
                            {
                                title: "成功仍可见", dataIndex: "success_still_visible", width: 100,
                                render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
                            },
                            { title: "时间", dataIndex: "updated_at", width: 170 },
                            {
                                title: "操作", width: 90,
                                render: (_, r) => (r.source_attachment_id || r.source_upload_file_id) ? (
                                    <Button size="small" type="link" onClick={() => jumpToEvidence(r)}>查看文件</Button>
                                ) : "—",
                            },
                        ]}
                    />
                )}
            </Card>
        </div>
    );

    const flightDateLabel = identity.flight_date
        ? String(identity.flight_date).slice(0, 10)
        : "—";

    return (
        <div className="page-container">
            {/* 不用 page-header-compact：其固定 height:32px 会裁切多行标题/标签并叠到下方卡片 */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 16,
                    flexWrap: "wrap",
                    marginBottom: 16,
                }}
            >
                <Space align="start" size={12} style={{ flex: 1, minWidth: 280 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回航班列表</Button>
                    <div style={{ minWidth: 0 }}>
                        <Title level={4} style={{ margin: "0 0 8px", lineHeight: 1.35 }}>
                            {identity.flight_number || "—"}
                            <Text type="secondary" style={{ fontSize: 16, fontWeight: 400, marginLeft: 8 }}>
                                {flightDateLabel} · {routeText}
                                {identity.aircraft_reg ? ` · ${identity.aircraft_reg}` : ""}
                            </Text>
                        </Title>
                        <Space size={8} wrap>
                            {statusTag(archiveStatus)}
                            <Tag>{fileHint}</Tag>
                            {identity.status && <Tag>{identity.status}</Tag>}
                        </Space>
                    </div>
                </Space>
                <Space wrap>
                    <Button onClick={() => revealSection("files")}>打开航班文件</Button>
                    {uploadControl}
                </Space>
            </div>

            <Row gutter={[10, 10]} className="cargo-archive-kpi-row">
                <Col xs={24} md={8}>
                    <Card size="small" className="cargo-archive-kpi">
                        <div className="cargo-archive-kpi__head">
                            <Text type="secondary">运单重量 · 舱单口径</Text>
                            {statusTag(fact.manifest?.status)}
                        </div>
                        <Statistic
                            value={fact.manifest?.weight_kg ?? undefined}
                            suffix="kg"
                            precision={0}
                            valueStyle={{ fontWeight: 700 }}
                            formatter={(v) => (v == null ? "—" : fmtNum(v))}
                        />
                        <Text className="cargo-archive-kpi__meta">
                            {fmtNum(fact.manifest?.pieces)} 件 · 体积 {fmtNum(fact.manifest?.volume_cbm, 2)} m³ · 来源 {fact.manifest?.source_count ?? 0}
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card size="small" className="cargo-archive-kpi">
                        <div className="cargo-archive-kpi__head">
                            <Text type="secondary">装载毛重 · 含板口径</Text>
                            {statusTag(fact.load?.status)}
                        </div>
                        <Statistic
                            value={fact.load?.gross_weight_kg ?? undefined}
                            suffix="kg"
                            precision={0}
                            valueStyle={{ fontWeight: 700 }}
                            formatter={(v) => (v == null ? "—" : fmtNum(v))}
                        />
                        <Text className="cargo-archive-kpi__meta">
                            净重 {fmtNum(fact.load?.net_weight_kg)} kg · 已占舱位 {fact.load?.positioned_uld_count ?? "—"} · 来源 {fact.load?.source_count ?? 0}
                        </Text>
                        {missingLabels.length > 0 && (
                            <div className="cargo-archive-kpi__missing">
                                <Tag color="orange">缺少{missingLabels.join("、")}</Tag>
                            </div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card size="small" className="cargo-archive-kpi">
                        <div className="cargo-archive-kpi__head">
                            <Text type="secondary">FWB 覆盖</Text>
                            {statusTag(fact.fwb?.status)}
                        </div>
                        <Statistic
                            value={`${fact.fwb?.linked_awb_count ?? 0} / ${fact.fwb?.awb_count ?? 0}`}
                            valueStyle={{ fontWeight: 700 }}
                        />
                        <Text className="cargo-archive-kpi__meta">
                            覆盖率 {((fact.fwb?.coverage_rate ?? 0) * 100).toFixed(0)}% · 不把舱单品名冒充 FWB
                        </Text>
                    </Card>
                </Col>
            </Row>

            {issues.length > 0 ? (
                <Alert
                    type={issues.some((i) => i.severity === "error") ? "error" : "warning"}
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`要处理（${issues.length}）`}
                    description={(
                        <List
                            size="small"
                            dataSource={issues}
                            renderItem={(it) => (
                                <List.Item style={{ padding: "4px 0", border: "none" }}>
                                    <Space>
                                        <Tag color={it.severity === "error" ? "red" : it.severity === "warning" ? "orange" : "blue"}>
                                            {it.severity}
                                        </Tag>
                                        <Text>{it.message}</Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    )}
                    action={(
                        <Button
                            size="small"
                            htmlType="button"
                            onClick={() => revealSection("load")}
                        >
                            看装载明细
                        </Button>
                    )}
                />
            ) : (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="暂无需要关注的问题。运单重量与装载毛重分口径并列，不对差。"
                />
            )}

            <Collapse
                className="cargo-archive-collapse"
                activeKey={openKeys}
                onChange={(keys) => setOpenKeys(Array.isArray(keys) ? keys : [keys].filter(Boolean))}
                items={[
                    {
                        key: "load",
                        id: sectionElementId("load"),
                        label: (
                            <span className="cargo-archive-section-label">
                                <span className="cargo-archive-section-label__title">装载与舱单明细</span>
                            </span>
                        ),
                        extra: <span className="cargo-archive-section-label__meta">ULD / AWB</span>,
                        children: loadPanel,
                    },
                    {
                        key: "files",
                        id: sectionElementId("files"),
                        label: (
                            <span className="cargo-archive-section-label">
                                <span className="cargo-archive-section-label__title">
                                    航班文件（{fileSummary.total_count || 0}）
                                </span>
                            </span>
                        ),
                        extra: <span className="cargo-archive-section-label__meta">下载 / 补传</span>,
                        children: filePanel,
                    },
                    {
                        key: "messages",
                        id: sectionElementId("messages"),
                        label: (
                            <span className="cargo-archive-section-label">
                                <span className="cargo-archive-section-label__title">报文原文</span>
                            </span>
                        ),
                        extra: <span className="cargo-archive-section-label__meta">仅标准正文</span>,
                        children: messagePanel,
                    },
                    {
                        key: "sources",
                        id: sectionElementId("sources"),
                        label: (
                            <span className="cargo-archive-section-label">
                                <span className="cargo-archive-section-label__title">来源与版本</span>
                            </span>
                        ),
                        extra: <span className="cargo-archive-section-label__meta">冲突 / 版本</span>,
                        children: sourcesPanel,
                    },
                ]}
            />
        </div>
    );
}
